import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CC, ccConfig } from './CC';
import * as ccUtils from './utils';

export type User = {
  userDetails: {
      email: string;
      realname: string;
      roles: string[];
  };
  token: string;
  tokenExpires: string; // If you want this to be a Date object, you'll need to convert the string to a Date when you receive it.
  albums: Album[];
};

export type Album = {
  albumId: string;
  albumName: string;
  albumParent: string;
  albumText: string;
  childNodes?: Album[];
};

export interface ICCContext {
  cc: CC;
  setCc: React.Dispatch<React.SetStateAction<CC>>;
  sort: string;
  setSort: React.Dispatch<React.SetStateAction<string>>;
  first: number;
  setFirst: React.Dispatch<React.SetStateAction<number>>;
  recordsPerPage: number;
  setRecordsPerPage: React.Dispatch<React.SetStateAction<number>>;
  oldRecordsPerPage: number;
  setOldRecordsPerPage: React.Dispatch<React.SetStateAction<number>>;
  viewMode: string;
  setViewMode: React.Dispatch<React.SetStateAction<string>>;
  browseMode: string;
  setBrowseMode: React.Dispatch<React.SetStateAction<string>>;
  data: { [key: string]: any };
  setData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  user: User;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  isReady: boolean;
  setReadyState: (state: boolean) => void;
}

export const CCContext = createContext<ICCContext | undefined>(undefined);

interface CCProviderProps {
  children: React.ReactNode;
}

/*

  http://localhost:3000/?action=addFacetValue&facetName=words&facetValue=bois
  http://localhost:3000/?action=activateFilter&filterName=objects
  http://localhost:3000/?action=search&field=/Record/Title&label=Title&value=masque

  https://88.99.164.61/pod23/?state={"Searches":[{"identifier":"simpleSearch","field":"All search fields","searchValues":[{"source":"freetext","text":"trois"}]}],"matchAllCriteria":false}
  https://88.99.164.61/pod23/?state={"Searches":[{"identifier":"simpleSearch","field":"All search fields","searchValues":[{"source":"freetext","text":"masque"}]},{"identifier":"advancedSearch0","field":"All search fields"},{"identifier":"advancedSearch1","field":"Title"},{"identifier":"advancedSearch2","field":"Inventory number"},{"identifier":"advancedSearch3","field":"Description"},{"identifier":"advancedSearch4","field":"Conservator"}],"matchAllCriteria":true}
  http://88.99.164.61/pod23?state=state_-1874972213

  http://localhost:3000?state={"Searches":[{"identifier":"simpleSearch","field":"All search fields","searchValues":[{"source":"freetext","text":"trois"}]}],"matchAllCriteria":false}
  http://localhost:3000?state={"Searches":[{"identifier":"simpleSearch","field":"All search fields","searchValues":[{"source":"freetext","text":"masque"}]},{"identifier":"advancedSearch0","field":"All search fields"},{"identifier":"advancedSearch1","field":"Title"},{"identifier":"advancedSearch2","field":"Inventory number"},{"identifier":"advancedSearch3","field":"Description"},{"identifier":"advancedSearch4","field":"Conservator"}],"matchAllCriteria":true}
  http://88.99.164.61/pod23?state=state_-1874972213

  */



export const CCProvider: React.FC<CCProviderProps> = ({ children }) => {
 
  const queryParams = new URLSearchParams(window.location.search);

  const defaultCCObj = new CC();
  let startUPCCObj = defaultCCObj;

  let startupViewMode = "homepage";

  const state = queryParams.get('state');
  if (state) {
    startupViewMode = "list";

    if (state.startsWith('state_')) {
      ccUtils.getStateFromServer(state)
        .then(retrievedCcState => {
          //console.log(JSON.stringify(defaultCCObj));
          //console.log(JSON.stringify(retrievedCcState));
          //console.log(JSON.stringify(ccUtils.deepMerge(defaultCCObj, retrievedCcState)));
          startUPCCObj = ccUtils.deepMerge(defaultCCObj, retrievedCcState);
        })
        .catch(error => {
          console.error("Error retrieving state from server:", error);
        });
    } else {
      try {
        startUPCCObj =  ccUtils.deepMerge(defaultCCObj, JSON.parse(state) as CC);
      } catch (error) {
        console.warn("Failed to parse state:", error);
        startUPCCObj =  defaultCCObj;
      }
    }
  }
  
  //http://localhost:3000/?action=search&field=/Record/ObjectNumber&label=N%C2%B0%20de%20gestion&value=71.1974.107.6.1-2
  const action = queryParams.get('action');

  const filterName = queryParams.get('filterName');
  const facetName = queryParams.get('facetName');
  const facetValue = queryParams.get('facetValue');

  // Get custom search parameters
  const customSearchField = queryParams.get('field');
  const customSearchValue = queryParams.get('value');
  const customSearchLabel = queryParams.get('label') || '';
  const customSearchDescription = queryParams.get('description') || '';

  switch (action) {
    case 'activateFilter':
      if (filterName) {
        //ccUtils.activateFilter(ctx, filterName);
      }
      break;
    case 'addFacetValue':
      if (facetName && facetValue) {
        //ccUtils.addFacetValue(ctx, facetName, facetValue);
      }
      break;
    case 'search':
      if (customSearchField && customSearchValue) {
        //ccUtils.customSearch(ctx, customSearchField, customSearchLabel, customSearchValue, customSearchDescription);
        const criterion = {
          field: customSearchField,
          fieldLabel: customSearchLabel,
          searchValues: ccUtils.ensureArray(customSearchValue),
          description: customSearchDescription
        };      
        const freshCC = new CC();
        freshCC.customSearchCriteria = [criterion];      
        startUPCCObj = ccUtils.deepMerge(defaultCCObj, freshCC);
        startupViewMode = "list";
      }
      break;
  } 
 
  const instanceRef = useRef({});
  const navigate = useNavigate();
  const location = useLocation();
  const initialCC = startUPCCObj;
  const [cc, setCc] = useState(initialCC);
  const [lastUrl, setLastUrl] = useState<string | null>(null);
  const [sort, setSort] = useState<string>(Object.keys(ccConfig.sort)[0]);
  const [first, setFirst] = useState<number>(1);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(24);
  const [oldRecordsPerPage, setOldRecordsPerPage] = useState<number>(24);
  const [viewMode, setViewMode] = useState<string>(startupViewMode);
  const [browseMode, setBrowseMode] = useState<string>("paginate");
  const [user, setUser] = useState<any | null>(null);
  const [data, setData] = useState<{ [key: string]: any }>({
    records: {
      record: []
    }
  });

  const [isReady, setIsReady] = useState(false);

  const prevCcPushed = useRef("");

  const setReadyState = (state: boolean) => {
    setIsReady(state);
  };

  const pushState = (cc: CC, sort: string, first: number, recordsPerPage: number, viewMode: string, browseMode: string) => {
    const state = { cc, sort, first, recordsPerPage, viewMode, browseMode };
    if (prevCcPushed.current !== JSON.stringify(state)) {
      prevCcPushed.current = JSON.stringify(state);
      navigate(window.location.pathname, { state });
    }
  };

  useEffect(() => {
    if (isReady) pushState(cc, sort, first, recordsPerPage, viewMode, browseMode);
  }, [cc, sort, first, recordsPerPage, viewMode, browseMode]);

  useEffect(() => {
    if (isReady && location?.state && JSON.stringify(location.state) !== JSON.stringify({ cc, sort, first, recordsPerPage, viewMode, browseMode })) {
      console.log('Location changed');
      console.log(location.state.cc);
      prevCcPushed.current = JSON.stringify(location.state);
      setCc(location.state.cc);
      setSort(location.state.sort);
      setFirst(location.state.first);
      setRecordsPerPage(location.state.recordsPerPage);
      setViewMode(location.state.viewMode);
      setBrowseMode(location.state.browseMode);
    }
  }, [location]);
  
  // http://localhost:3000/?state={%22Searches%22:[{%22identifier%22:%22simpleSearch%22,%22field%22:%22All%20search%20fields%22}],%22matchAllCriteria%22:false,%22facets%22:{%22categorie%22:[%22Textile%20ou%20v%C3%AAtement%22]}}
  
  const search = async (cc: CC) => {
    const payload = ccUtils.generatePayload(cc, sort, first, first + recordsPerPage - 1);
    //console.log("searchpayload", payload);
    try {
      const responseData = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, payload, instanceRef);
      //console.log("searchpayload", responseData);
      setData(responseData);
      setIsReady(true);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    //if (isReady) 
    search(cc);
    //console.log(ccUtils.GenerateStateHyperlink(cc));
  }, [cc, first, recordsPerPage]);

  return (
    <CCContext.Provider
      value={{ cc, setCc, sort, setSort, first, setFirst, recordsPerPage, setRecordsPerPage, oldRecordsPerPage, setOldRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData, user, setUser, isReady, setReadyState }}
    >
      {children}
    </CCContext.Provider>
  );
};

export const useCCContext = () => {
  const context = useContext(CCContext);
  if (!context) {
    throw new Error('useCCContext must be used within a CCProvider');
  }
  return context;
};
